import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { runningUnderNativePlatform } from 'src/store/running-under-native-platform.js';
import type {
  AuthenticatedSession,
  PersistedAuthIdentity,
  PersistedAuthState,
  StoredLogoutReturnState,
} from './auth-storage-types.js';

export const AUTH_USERNAME_KEY = 'dabih-username';
export const AUTH_PROVIDER_KEY = 'dabih-provider';
export const AUTH_ADMIN_TOKEN_KEY = 'dabih-token';
export const AUTH_ISSUED_KEY = 'dabih-issued';
export const ACCESS_TOKEN_KEY = 'access_token';
export const REFRESH_TOKEN_KEY = 'refresh_token';
export const OAUTH_PROCESSING_KEY = 'oauth-processing';
export const POST_LOGOUT_REDIRECT_TO_SIGNIN_KEY = 'postLogoutRedirectToSignin';
export const POST_LOGOUT_PROVIDER_KEY = 'postLogoutProvider';
export const PENDING_CRIIPTO_LOGOUT_RETURN_KEY = 'pendingCriiptoLogoutReturn';
export const AUTH_TARGET_URL_KEY = 'targetUrl';
export const AUTH_TARGET_PAGE_ID_KEY = 'targetPageId';
export const LAST_ORGANIZATION_ID_KEY = 'lastOrganizationId';
export const MOBILE_NAVIGATION_HISTORY_KEY = 'history';
export const MOBILE_NAVIGATION_HISTORY_INDEX_KEY = 'historyIndex';

const isNative = runningUnderNativePlatform();

export async function saveTokens(accessToken: string, refreshToken: string) {
  if (isNative) {
    await SecureStoragePlugin.set({ key: ACCESS_TOKEN_KEY, value: accessToken });
    await SecureStoragePlugin.set({ key: REFRESH_TOKEN_KEY, value: refreshToken });
  } else {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  localStorage.removeItem(AUTH_ADMIN_TOKEN_KEY);
}

export async function hasAccessToken(): Promise<boolean> {
  if (isNative) {
    const keys = await SecureStoragePlugin.keys();
    return keys.value.includes(ACCESS_TOKEN_KEY);
  }

  return localStorage.getItem(ACCESS_TOKEN_KEY) !== null;
}

export async function getAccessToken() {
  if (isNative) {
    try {
      const { value } = await SecureStoragePlugin.get({ key: ACCESS_TOKEN_KEY });
      return value;
    } catch {
      return '';
    }
  }

  return localStorage.getItem(ACCESS_TOKEN_KEY) || '';
}

export async function getRefreshToken() {
  if (isNative) {
    try {
      const { value } = await SecureStoragePlugin.get({ key: REFRESH_TOKEN_KEY });
      return value;
    } catch {
      return '';
    }
  }

  return localStorage.getItem(REFRESH_TOKEN_KEY) || '';
}

export async function clearTokens() {
  if (isNative) {
    await SecureStoragePlugin.clear();
  } else {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  }

  localStorage.removeItem(AUTH_ADMIN_TOKEN_KEY);
}

export function setPersistedAuthIdentity(identity: PersistedAuthIdentity & { issuedAt?: string }) {
  if (identity.username !== undefined) {
    localStorage.setItem(AUTH_USERNAME_KEY, identity.username);
  } else {
    localStorage.removeItem(AUTH_USERNAME_KEY);
  }

  if (identity.provider !== undefined) {
    localStorage.setItem(AUTH_PROVIDER_KEY, identity.provider);
  } else {
    localStorage.removeItem(AUTH_PROVIDER_KEY);
  }

  if (identity.issuedAt !== undefined) {
    localStorage.setItem(AUTH_ISSUED_KEY, identity.issuedAt);
  }
}

export function clearPersistedAuthIdentity() {
  localStorage.removeItem(AUTH_USERNAME_KEY);
  localStorage.removeItem(AUTH_PROVIDER_KEY);
  localStorage.removeItem(AUTH_ISSUED_KEY);
}

export function getPersistedAuthIdentity(): PersistedAuthIdentity {
  return {
    username: localStorage.getItem(AUTH_USERNAME_KEY) ?? undefined,
    provider: localStorage.getItem(AUTH_PROVIDER_KEY) ?? undefined,
  };
}

export async function getPersistedAuthState(): Promise<PersistedAuthState> {
  const { username, provider } = getPersistedAuthIdentity();
  const accessToken = await hasAccessToken();
  const refreshToken = await getRefreshToken();
  const hasAdminToken = localStorage.getItem(AUTH_ADMIN_TOKEN_KEY) !== null;
  const hasPersistedAuth = accessToken || refreshToken !== '' || hasAdminToken;

  if (!hasPersistedAuth && username !== undefined) {
    clearPersistedAuthIdentity();
    return {
      username: undefined,
      provider: undefined,
      hasPersistedAuth,
    };
  }

  return {
    username,
    provider,
    hasPersistedAuth,
  };
}

export async function persistAuthenticatedSession(session: AuthenticatedSession) {
  setPersistedAuthIdentity({
    username: session.username,
    provider: session.provider,
    issuedAt: new Date().toISOString(),
  });
  await saveTokens(session.accessToken, session.refreshToken);
}

export function markOAuthProcessing() {
  sessionStorage.setItem(OAUTH_PROCESSING_KEY, 'true');
}

export function clearOAuthProcessing() {
  sessionStorage.removeItem(OAUTH_PROCESSING_KEY);
}

export function isOAuthProcessing() {
  return sessionStorage.getItem(OAUTH_PROCESSING_KEY) === 'true';
}

export function getStoredLogoutReturnState(): StoredLogoutReturnState {
  return {
    pendingCriiptoLogoutReturn: sessionStorage.getItem(PENDING_CRIIPTO_LOGOUT_RETURN_KEY) === 'true',
    postLogoutRedirectToSignin: sessionStorage.getItem(POST_LOGOUT_REDIRECT_TO_SIGNIN_KEY) === 'true',
    postLogoutProvider: sessionStorage.getItem(POST_LOGOUT_PROVIDER_KEY) ?? undefined,
  };
}

export function persistLogoutReturnState(state: { postLogoutRedirectToSignin?: 'true'; postLogoutProvider?: string }) {
  if (state.postLogoutRedirectToSignin !== undefined) {
    sessionStorage.setItem(POST_LOGOUT_REDIRECT_TO_SIGNIN_KEY, state.postLogoutRedirectToSignin);
  }

  if (state.postLogoutProvider !== undefined) {
    sessionStorage.setItem(POST_LOGOUT_PROVIDER_KEY, state.postLogoutProvider);
    sessionStorage.setItem(PENDING_CRIIPTO_LOGOUT_RETURN_KEY, 'true');
  }
}

export function clearLogoutReturnState() {
  sessionStorage.removeItem(POST_LOGOUT_REDIRECT_TO_SIGNIN_KEY);
  sessionStorage.removeItem(POST_LOGOUT_PROVIDER_KEY);
  sessionStorage.removeItem(PENDING_CRIIPTO_LOGOUT_RETURN_KEY);
}

export function clearPendingCriiptoLogoutReturn() {
  sessionStorage.removeItem(PENDING_CRIIPTO_LOGOUT_RETURN_KEY);
}

export function clearPostLogoutProvider() {
  sessionStorage.removeItem(POST_LOGOUT_PROVIDER_KEY);
}

export function clearPostLogoutRedirectToSignin() {
  sessionStorage.removeItem(POST_LOGOUT_REDIRECT_TO_SIGNIN_KEY);
}

export function clearAuthFlowTarget() {
  sessionStorage.removeItem(AUTH_TARGET_URL_KEY);
  sessionStorage.removeItem(AUTH_TARGET_PAGE_ID_KEY);
}

export function clearAuthNavigationState() {
  localStorage.removeItem(LAST_ORGANIZATION_ID_KEY);
  sessionStorage.removeItem(MOBILE_NAVIGATION_HISTORY_KEY);
  sessionStorage.removeItem(MOBILE_NAVIGATION_HISTORY_INDEX_KEY);
}

export async function clearAuthStorage() {
  clearPersistedAuthIdentity();
  clearOAuthProcessing();
  clearLogoutReturnState();
  clearAuthFlowTarget();
  clearAuthNavigationState();
  await clearTokens();
}
